import React, { useState, useEffect, useCallback } from "react";
import { X, Play, Pause } from "@phosphor-icons/react";

/**
 * WatchDemo — a video-style, auto-playing walkthrough of Alaine's Bench.
 * Plays real product screenshots with slow Ken Burns motion and caption
 * overlays, like a demo video. Zero product interaction — viewers watch,
 * they don't click around inside the studio.
 *
 * Usage: const [open, setOpen] = useState(false);
 *        <button onClick={() => setOpen(true)}>Watch the demo</button>
 *        {open && <WatchDemo onClose={() => setOpen(false)} />}
 */

const BASE = process.env.PUBLIC_URL || "";
const SCENE_MS = 6000;

const SCENES = [
  {
    img: "dashboard.png",
    kicker: "Your studio",
    title: "Every formula, one dashboard",
    caption: "All your products live in one place — formulas, labels, and safety docs stay in sync as you work.",
  },
  {
    img: "builder.png",
    kicker: "Formulation Builder",
    title: "Build recipes down to the gram",
    caption: "Add ingredients, set batch sizes, and scale with precision. The Bench recalculates everything instantly.",
  },
  {
    img: "label.png",
    fit: "contain", // tall label — show the whole thing
    kicker: "Nutrition labeling",
    title: "FDA-compliant labels, automatically",
    caption: "Nutrition Facts built on the FDA's own rounding rules — allergens and ingredient statements included.",
  },
  {
    img: "safety.png",
    kicker: "Food safety",
    title: "Safety guard-rails built in",
    caption: "pH and acidity warnings while you formulate, plus a pre-filled draft Food Safety Plan for your QA reviewer.",
  },
  {
    img: "flavor-radar.png",
    fit: "contain", // very wide & short — cover would zoom into blank space
    kicker: "Flavor Radar",
    title: "See your flavor profile",
    caption: "A six-axis sensory radar computed from your formula — watch your product's flavor balance take shape.",
  },
  {
    img: "cost.png",
    fit: "contain", // portrait screenshot
    kicker: "Costing & margins",
    title: "Know your numbers before you launch",
    caption: "Ingredient costs, batch costs, and margin targets — priced out while the recipe is still on the bench.",
  },
  {
    img: "organic.png",
    kicker: "Organic claims",
    title: "Know which organic claim you've earned",
    caption: "The Bench calculates your organic percentage and shows the claim tier your formula qualifies for.",
  },
  {
    img: "commercialization.png",
    kicker: "Commercialization",
    title: "A package co-manufacturers understand",
    caption: "One click assembles your formula, label, and specs into a package ready to send to a co-man.",
  },
  {
    img: "starters.png",
    kicker: "Starter formulas",
    title: "Never start from a blank page",
    caption: "Begin from proven starter formulations — sauces, seasonings, snacks — then make them yours.",
  },
];

export default function WatchDemo({ onClose }) {
  const [scene, setScene] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [tick, setTick] = useState(0); // restarts progress animation

  const advance = useCallback(() => {
    setScene((s) => (s + 1) % SCENES.length);
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (!playing) return undefined;
    const id = setTimeout(advance, SCENE_MS);
    return () => clearTimeout(id);
  }, [scene, playing, tick, advance]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Preload the next image so transitions are seamless.
  useEffect(() => {
    const next = SCENES[(scene + 1) % SCENES.length];
    const im = new Image();
    im.src = `${BASE}/images/tour/${next.img}`;
  }, [scene]);

  const s = SCENES[scene];

  return (
    <div className="fixed inset-0 z-[100] bg-emerald-950/95 backdrop-blur-sm flex flex-col" data-testid="watch-demo-overlay"
      role="dialog" aria-modal="true" aria-label="Alaine's Bench demo video">
      <style>{`
        @keyframes wd-kenburns { from { transform: scale(1) translateY(0); } to { transform: scale(1.06) translateY(-1.5%); } }
        @keyframes wd-fadein { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes wd-progress { from { width: 0%; } to { width: 100%; } }
      `}</style>

      {/* Top bar */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3">
        <div className="flex items-center gap-2 text-white">
          <div className="h-7 w-7 rounded-md bg-emerald-700 grid place-items-center font-bold text-sm">A</div>
          <div className="font-semibold text-sm sm:text-base">Alaine's Bench<sup className="text-[0.55em] ml-0.5 align-super">™</sup> — Demo</div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close demo" data-testid="watch-demo-close"
          className="text-white/70 hover:text-white p-2 rounded-md hover:bg-white/10 transition-colors">
          <X size={22} weight="bold" />
        </button>
      </div>

      {/* Stage */}
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-4 sm:px-8 pb-4">
        <div className="w-full max-w-4xl">
          <div className="relative rounded-xl overflow-hidden shadow-2xl bg-stone-900 border border-white/10" style={{ aspectRatio: "16/10" }}>
            <img
              key={scene}
              src={`${BASE}/images/tour/${s.img}`}
              alt={s.title}
              className={`absolute inset-0 w-full h-full ${s.fit === "contain" ? "object-contain bg-white p-4 sm:p-6" : "object-cover object-top"}`}
              style={{ animation: playing ? `wd-kenburns ${SCENE_MS}ms ease-out forwards` : "none" }}
              draggable={false}
            />
            {/* Caption overlay */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent pt-16 pb-4 px-4 sm:px-6">
              <div key={`cap-${scene}`} style={{ animation: "wd-fadein 500ms ease-out both" }}>
                <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-emerald-300 font-bold">{s.kicker}</div>
                <div className="text-white font-bold text-lg sm:text-2xl mt-1 leading-tight">{s.title}</div>
                <div className="text-white/85 text-xs sm:text-sm mt-1.5 leading-relaxed max-w-2xl">{s.caption}</div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="mt-4 flex items-center gap-3">
            <button type="button" onClick={() => setPlaying((p) => !p)} data-testid="watch-demo-playpause"
              aria-label={playing ? "Pause" : "Play"}
              className="h-10 w-10 shrink-0 rounded-full bg-white text-emerald-900 grid place-items-center hover:bg-emerald-50 transition-colors">
              {playing ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
            </button>
            <div className="flex-1 flex items-center gap-1.5">
              {SCENES.map((_, i) => (
                <button type="button" key={i} onClick={() => { setScene(i); setTick((t) => t + 1); setPlaying(true); }}
                  aria-label={`Chapter ${i + 1}`}
                  className="flex-1 h-1.5 rounded-full bg-white/25 overflow-hidden hover:h-2.5 transition-all">
                  <div className="h-full bg-white rounded-full"
                    style={i < scene ? { width: "100%" } : i === scene ? {
                      animation: playing ? `wd-progress ${SCENE_MS}ms linear forwards` : "none",
                      width: playing ? undefined : "35%",
                    } : { width: "0%" }}
                    key={`p-${scene}-${tick}`}
                  />
                </button>
              ))}
            </div>
            <div className="text-white/60 text-xs font-mono tabular-nums shrink-0">{scene + 1}/{SCENES.length}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
